import {
  type JoystickMoveIntent,
  type ViewerAnimState,
  type ViewerNavigationApi,
  type ViewerNavigationMode,
  type WalkMoveIntent,
  type ViewerAnimTrack,
} from '@mindcloudxai/shared';
import type { ViewerContext } from '../types';
import { toSettingsAnimTrack } from '../settings';
import { AnimController } from '../../../submodules/supersplat-viewer/src/cameras/anim-controller';

export function createNavigationApi(
  getCtx: () => ViewerContext,
  jump: () => void,
): ViewerNavigationApi {
  return {
    getMode: () => getNavigationMode(getCtx()),
    setMode: (mode) => setNavigationMode(getCtx(), mode),
    reset: () => resetNavigation(getCtx()),
    isWalkAvailable: () => isWalkAvailable(getCtx()),
    walk: {
      setMoveIntent: (intent) => setWalkMoveIntent(getCtx(), intent),
      setJoystickIntent: (intent) => setJoystickMoveIntent(getCtx(), intent),
      jump,
    },
    anim: {
      getState: () => getAnimState(getCtx()),
      play: () => playAnim(getCtx()),
      pause: () => pauseAnim(getCtx()),
      togglePlayPause: () => togglePlayPause(getCtx()),
      seek: (time) => seekAnim(getCtx(), time),
      setSpeed: (speed) => setAnimSpeed(getCtx(), speed),
      setTrack: (track) => setAnimTrack(getCtx(), track),
    },
  };
}

function getNavigationMode(ctx: ViewerContext): ViewerNavigationMode {
  return (ctx.global?.state.cameraMode ?? 'orbit') as ViewerNavigationMode;
}

function setNavigationMode(ctx: ViewerContext, mode: ViewerNavigationMode): void {
  if (!ctx.global) return;
  // walk/avatar 需要碰撞数据；直接写 state 会绕过 toggleWalk 门控，降级为 fly，
  // 实际模式经 navigationMode:changed 回流
  if ((mode === 'walk' || mode === 'avatar') && !ctx.global.state.walkAllowed) {
    mode = 'fly';
  }
  // observe 代理在值未变化时不触发事件，天然防止循环
  ctx.global.state.cameraMode = mode;
}

function resetNavigation(ctx: ViewerContext): void {
  if (!ctx.global) return;
  ctx.global.events.fire('inputEvent', 'reset');
  ctx.app.renderNextFrame = true;
}

function isWalkAvailable(ctx: ViewerContext): boolean {
  return ctx.global?.state.walkAllowed ?? false;
}

function getAnimState(ctx: ViewerContext): ViewerAnimState {
  const state = ctx.global?.state;
  if (!state) {
    return { hasAnimation: false, duration: 0, time: 0, paused: true, active: false };
  }
  return {
    hasAnimation: state.hasAnimation,
    duration: state.animationDuration,
    time: state.animationTime,
    paused: state.animationPaused,
    active: state.cameraMode === 'anim',
  };
}

function playAnim(ctx: ViewerContext): void {
  const state = ctx.global?.state;
  if (!state?.hasAnimation) return;
  state.cameraMode = 'anim';
  state.animationPaused = false;
  ctx.app.renderNextFrame = true;
}

function pauseAnim(ctx: ViewerContext): void {
  const state = ctx.global?.state;
  if (!state?.hasAnimation) return;
  state.animationPaused = true;
}

/** 与 ModeShortcuts Space 键相同语义：anim 模式下 toggle pause，非 anim 模式切入播放。 */
function togglePlayPause(ctx: ViewerContext): void {
  if (!ctx.global) return;
  ctx.global.events.fire('inputEvent', 'playPause');
}

function seekAnim(ctx: ViewerContext, time: number): void {
  const state = ctx.global?.state;
  if (!state?.hasAnimation || !ctx.viewer) return;
  const clamped = Math.max(0, Math.min(time, state.animationDuration));
  ctx.global?.events.fire('scrubAnim', clamped);
  ctx.app.renderNextFrame = true;
}

function setAnimSpeed(ctx: ViewerContext, speed: number): void {
  if (!ctx.global) return;
  ctx.global.events.fire('setAnimSpeed', Math.max(0.05, speed));
}

function setAnimTrack(ctx: ViewerContext, track: ViewerAnimTrack | null): void {
  const global = ctx.global;
  const viewer = ctx.viewer as any;
  if (!global || !viewer) return;

  if (!track) {
    global.state.hasAnimation = false;
    global.state.animationDuration = 0;
    global.state.animationTime = 0;
    global.state.animationPaused = true;
    if (global.state.cameraMode === 'anim') {
      global.state.cameraMode = 'fly';
    }
    if (viewer.cameraManager?.controllers) {
      viewer.cameraManager.controllers.anim = null;
    }
    ctx.events.fire('anim:changed', getAnimState(ctx));
    ctx.app.renderNextFrame = true;
    return;
  }

  const settingsTrack = toSettingsAnimTrack(track);
  if (viewer.cameraManager?.controllers) {
    viewer.cameraManager.controllers.anim = new AnimController(settingsTrack);
  }

  global.state.hasAnimation = true;
  global.state.animationDuration = track.duration;

  if (global.state.cameraMode === 'anim') {
    global.events.fire('scrubAnim', 0);
  }

  ctx.app.renderNextFrame = true;
}

function setWalkMoveIntent(ctx: ViewerContext, intent: WalkMoveIntent): void {
  setJoystickMoveIntent(ctx, {
    x: intent.right,
    y: -intent.forward,
  });
}

function setJoystickMoveIntent(ctx: ViewerContext, intent: JoystickMoveIntent): void {
  if (!ctx.global) return;
  ctx.global.state.gamingControls = true;
  ctx.global.events.fire('joystickInput', {
    x: Math.max(-1, Math.min(1, intent.x)),
    y: Math.max(-1, Math.min(1, intent.y)),
  });
  if (intent.sprint !== undefined) {
    ctx.global.events.fire('joystickSprint', intent.sprint);
  }
  if (intent.altitude !== undefined) {
    ctx.global.events.fire('joystickAltitude', Math.max(-1, Math.min(1, intent.altitude)));
  }
}
